import type { AiCandidateStatus } from "./types";

export type AiWorkspaceFilter = "ALL" | Exclude<AiCandidateStatus, "GENERATING">;

export interface AiWorkspaceState {
  version: 1;
  batchId: string;
  page: number;
  filter: AiWorkspaceFilter;
  selectedId: string;
}

interface WorkspaceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const AI_WORKSPACE_STORAGE_KEY = "meliforge-ai-workspace-v1";
const LEGACY_BATCH_STORAGE_KEY = "meliforge-ai-batch";
const validFilters = new Set<AiWorkspaceFilter>([
  "ALL",
  "PENDING_REVIEW",
  "APPROVED",
  "REJECTED",
  "GENERATION_FAILED",
  "SUBMITTED",
]);

const emptyWorkspace: AiWorkspaceState = {
  version: 1,
  batchId: "",
  page: 1,
  filter: "ALL",
  selectedId: "",
};

export function readAiWorkspaceState(storage: Pick<WorkspaceStorage, "getItem">): AiWorkspaceState {
  try {
    const raw = storage.getItem(AI_WORKSPACE_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (parsed.version === 1) {
        return {
          version: 1,
          batchId: typeof parsed.batchId === "string" ? parsed.batchId : "",
          page: typeof parsed.page === "number" && Number.isInteger(parsed.page) && parsed.page > 0 ? parsed.page : 1,
          filter: typeof parsed.filter === "string" && validFilters.has(parsed.filter as AiWorkspaceFilter)
            ? parsed.filter as AiWorkspaceFilter
            : "ALL",
          selectedId: typeof parsed.selectedId === "string" ? parsed.selectedId : "",
        };
      }
    }
  } catch {
    // A corrupt or unavailable browser store should never block server-side task recovery.
  }

  try {
    return { ...emptyWorkspace, batchId: storage.getItem(LEGACY_BATCH_STORAGE_KEY) || "" };
  } catch {
    return { ...emptyWorkspace };
  }
}

export function writeAiWorkspaceState(storage: Pick<WorkspaceStorage, "setItem">, state: Omit<AiWorkspaceState, "version">) {
  try {
    storage.setItem(AI_WORKSPACE_STORAGE_KEY, JSON.stringify({ version: 1, ...state } satisfies AiWorkspaceState));
    storage.setItem(LEGACY_BATCH_STORAGE_KEY, state.batchId);
  } catch {
    // Task data remains recoverable from the server when browser storage is unavailable.
  }
}
